import {tmText} from './tm-i18n.js';
const GENERIC_REASON = ({de: 'In der Auswahl verfügbar', it: 'Disponibile nella selezione', en: 'Available in the selection'})[typeof document === 'undefined' ? 'fr' : document.documentElement.lang.split('-')[0]] || 'Disponible dans la sélection';
const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const homeInitialOrder = new WeakMap();
const cartState = new WeakMap();

function productKey(product) {
  return String(product?.id || product?.handle || '');
}

function setCommercialReason(container, reason) {
  if (!(container instanceof HTMLElement)) return;
  container.querySelector('.tm-fragrance-commercial-reason')?.remove();
  if (!reason) return;
  const label = document.createElement('p');
  label.className = 'tm-fragrance-commercial-reason';
  label.textContent = reason;
  const anchor = container.querySelector('.tm-commerce-product__vendor, .tm-personal-picks__vendor, .tm-cart-recommendation__why');
  if (anchor) anchor.insertAdjacentElement('afterend', label);
  else container.append(label);
}

function itemProductKey(item) {
  return String(item?.product_id || item?.productId || '');
}

export function hasMeaningfulMatch(entry) {
  return Boolean(
    entry?.eligible &&
    Number.isFinite(Number(entry.score)) &&
    Number(entry.score) > 1 &&
    (entry.reasons || []).some((reason) => reason && reason !== GENERIC_REASON)
  );
}

export function chooseCartUpsell(entries, cart, thresholdCents, profile = {}) {
  if (cart?.requires_shipping === false || cartHasGiftCard(cart)) return null;
  const total = Number(cart?.total_price || 0);
  const threshold = Number(thresholdCents || 0);
  if (!Number.isFinite(total) || !Number.isFinite(threshold) || threshold <= total) return null;

  const remaining = threshold - total;
  const cartProducts = new Set((cart?.items || []).map(itemProductKey));
  const disliked = new Set((profile.disliked || []).map(String));
  const budgetMax = profile.budgetMax == null ? null : Number(profile.budgetMax);

  const matches = [];
  (entries || []).forEach((entry) => {
    const key = productKey(entry?.product);
    if (!hasMeaningfulMatch(entry) || !key || cartProducts.has(key) || disliked.has(key) || !entry.product?.available) return;

    const variants = (entry.product.variants || [])
      .filter((variant) => {
        const price = Number(variant?.price);
        return variant?.available && Number.isFinite(price) && price >= remaining && (budgetMax == null || price <= budgetMax);
      })
      .sort((a, b) => Number(a.price) - Number(b.price));

    if (variants[0]) matches.push({...entry, variant: variants[0]});
  });

  return matches.sort((a, b) =>
    Number(b.score) - Number(a.score) ||
    (Number(a.variant.price) - remaining) - (Number(b.variant.price) - remaining)
  )[0] || null;
}

function cartHasGiftCard(cart) {
  return (cart?.items || []).some((item) => item?.gift_card === true || item?.requires_shipping === false);
}

export function selectPersonalPicks(entries, currentProductId, limit = 3) {
  const current = String(currentProductId || '');
  return (entries || [])
    .filter((entry) => hasMeaningfulMatch(entry) && productKey(entry.product) !== current)
    .slice(0, Math.max(0, limit));
}

export function rankHomeProductGroups(groups, rankProducts) {
  return (groups || []).map((products) => {
    const candidates = Array.isArray(products) ? products : [];
    return rankProducts(candidates, candidates.length);
  });
}

function findProduct(engine, element) {
  const id = String(element.dataset.tmProductId || '');
  const handle = element.dataset.tmProductHandle || '';
  return engine.catalogue.find((product) => String(product.id) === id || product.handle === handle);
}

function restoreHomeOrder(grid, cards) {
  [...cards]
    .sort((a, b) => Number(a.dataset.tmInitialOrder) - Number(b.dataset.tmInitialOrder))
    .forEach((card) => grid.append(card));
}

function updateHomeSections(engine) {
  document.querySelectorAll('[data-tm-home-products]').forEach((section) => {
    const productGroups = [...section.querySelectorAll('[data-tm-home-product-group]')];
    const groups = productGroups.length ? productGroups : [section];
    const groupStates = groups.map((group) => {
      const grid = group.querySelector('[data-tm-home-product-grid]');
      const cards = grid ? [...grid.querySelectorAll('[data-tm-home-product]')] : [];
      if (!grid || cards.length === 0) return null;
      if (!homeInitialOrder.has(grid)) {
        cards.forEach((card, index) => { card.dataset.tmInitialOrder = String(index); });
        homeInitialOrder.set(grid, true);
      }
      return {grid, cards, products: cards.map((card) => findProduct(engine, card)).filter(Boolean)};
    }).filter(Boolean);
    if (groupStates.length === 0) return;

    const note = section.querySelector('[data-tm-personalized-note]');
    if (!engine.hasPreferences()) {
      groupStates.forEach(({grid, cards}) => {
        restoreHomeOrder(grid, cards);
        cards.forEach((card) => setCommercialReason(card.querySelector('.tm-commerce-product__copy'), null));
      });
      if (note instanceof HTMLElement) note.hidden = true;
      return;
    }

    const rankedGroups = rankHomeProductGroups(
      groupStates.map(({products}) => products),
      (products, limit) => engine.rank(products, limit)
    );
    groupStates.forEach(({grid, cards}, groupIndex) => {
      const rankByKey = new Map((rankedGroups[groupIndex] || []).map((entry, index) => [productKey(entry.product), {...entry, index}]));
      cards
        .map((card) => {
          const product = findProduct(engine, card);
          const result = product ? rankByKey.get(productKey(product)) : null;
          setCommercialReason(card.querySelector('.tm-commerce-product__copy'), result?.commercialReason);
          return {card, result, initial: Number(card.dataset.tmInitialOrder)};
        })
        .sort((a, b) => {
          const aEligible = hasMeaningfulMatch(a.result) ? 1 : 0;
          const bEligible = hasMeaningfulMatch(b.result) ? 1 : 0;
          return bEligible - aEligible || Number(a.result?.index ?? Infinity) - Number(b.result?.index ?? Infinity) || a.initial - b.initial;
        })
        .forEach(({card}) => grid.append(card));
    });

    if (note instanceof HTMLElement) note.hidden = false;
  });
}

function createPickCard(entry) {
  const {product, variant, reasons, commercialReason} = entry;
  const url = variant?.id ? `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variant.id)}` : product.url;
  const article = document.createElement('article');
  article.className = 'tm-personal-picks__card';

  const link = document.createElement('a');
  link.className = 'tm-personal-picks__media';
  link.href = url;
  link.setAttribute('aria-label', product.title);

  if (product.image?.src) {
    const image = document.createElement('img');
    image.src = product.image.src;
    image.alt = product.image.alt || product.title;
    image.loading = 'lazy';
    image.width = product.image.width || 480;
    image.height = product.image.height || 600;
    link.append(image);
  }

  const copy = document.createElement('div');
  copy.className = 'tm-personal-picks__copy';
  const vendor = document.createElement('p');
  vendor.className = 'tm-personal-picks__vendor';
  vendor.textContent = product.vendor || '';
  const title = document.createElement('h3');
  const titleLink = document.createElement('a');
  titleLink.href = url;
  titleLink.textContent = product.title;
  title.append(titleLink);
  const price = document.createElement('p');
  price.className = 'tm-personal-picks__price';
  price.textContent = variant?.priceFormatted || product.priceFormatted || '';
  const variantTitle = document.createElement('p');
  variantTitle.className = 'tm-personal-picks__variant';
  variantTitle.textContent = variant?.title && variant.title !== 'Default Title' ? variant.title : tmText('standard_format');
  const why = document.createElement('p');
  why.className = 'tm-personal-picks__why';
  why.textContent = (reasons || []).filter((reason) => reason !== GENERIC_REASON).slice(0, 2).join(' · ');
  copy.append(vendor, title, variantTitle, price, why);
  setCommercialReason(copy, commercialReason);
  article.append(link, copy);
  return article;
}

function updatePersonalPicks(engine) {
  document.querySelectorAll('[data-tm-personal-picks]').forEach((section) => {
    const grid = section.querySelector('[data-tm-personal-picks-grid]');
    if (!grid) return;

    if (!engine.hasPreferences()) {
      section.hidden = true;
      grid.replaceChildren();
      return;
    }

    const currentId = section.dataset.currentProductId || '';
    const candidates = engine.catalogue.filter((product) => productKey(product) !== String(currentId));
    const ranked = engine.rank(candidates, candidates.length);
    const picks = selectPersonalPicks(ranked, currentId, Number(section.dataset.limit || 3));

    if (picks.length === 0) {
      section.hidden = true;
      grid.replaceChildren();
      return;
    }

    grid.replaceChildren(...picks.map(createPickCard));
    section.hidden = false;
  });
}

function showFallback(section, cart, threshold) {
  const fallback = section.querySelector('[data-tm-cart-fallback]');
  const personalized = section.querySelector('[data-tm-cart-personalized]');
  const message = section.querySelector('[data-tm-cart-message]');
  const fallbackProductId = String(fallback?.dataset.tmProductId || '');
  const fallbackPrice = Number(fallback?.dataset.tmVariantPrice || 0);
  const productInCart = (cart?.items || []).some((item) => itemProductKey(item) === fallbackProductId);
  const total = Number(cart?.total_price || 0);
  const remainingAmount = Number(threshold) - total;
  const canShow = fallback instanceof HTMLElement &&
    Number(cart?.item_count || 0) > 0 &&
    remainingAmount > 0 &&
    fallbackPrice >= remainingAmount &&
    !productInCart;

  if (fallback instanceof HTMLElement) {
    fallback.hidden = !canShow;
    const impact = fallback.querySelector('[data-tm-cart-fallback-impact]');
    if (impact) impact.textContent = tmText('cart_estimate', {amount: formatMoney(total + fallbackPrice, 'CHF')});
  }
  const remaining = section.querySelector('[data-tm-cart-remaining]');
  if (remaining) remaining.textContent = tmText('remaining_threshold', {amount: formatMoney(remainingAmount, 'CHF')});
  if (personalized instanceof HTMLElement) personalized.hidden = true;
  if (message instanceof HTMLElement) message.hidden = true;
  section.hidden = !canShow;
}

function showNoCartMatch(section) {
  const fallback = section.querySelector('[data-tm-cart-fallback]');
  const personalized = section.querySelector('[data-tm-cart-personalized]');
  const message = section.querySelector('[data-tm-cart-message]');
  if (fallback instanceof HTMLElement) fallback.hidden = true;
  if (personalized instanceof HTMLElement) personalized.hidden = true;
  if (message instanceof HTMLElement) message.hidden = false;
  section.hidden = false;
}

function formatMoney(cents, format) {
  const value = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(`${(document.documentElement.lang || 'fr').split('-')[0]}-CH`, {
      style: 'currency', currency: 'CHF', currencyDisplay: 'code',
    }).format(value);
  } catch (_error) {
    return `${format || 'CHF'} ${value.toFixed(2)}`;
  }
}

function showCartMatch(section, match, cart, threshold) {
  const fallback = section.querySelector('[data-tm-cart-fallback]');
  const card = section.querySelector('[data-tm-cart-personalized]');
  const message = section.querySelector('[data-tm-cart-message]');
  if (!(card instanceof HTMLElement)) return;
  if (fallback instanceof HTMLElement) fallback.hidden = true;
  if (message instanceof HTMLElement) message.hidden = true;

  const {product, variant, reasons, commercialReason} = match;
  const url = `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variant.id)}`;
  const imageLink = card.querySelector('[data-tm-cart-link]');
  const titleLink = card.querySelector('[data-tm-cart-title]');
  const image = card.querySelector('[data-tm-cart-image]');
  const variantTitle = card.querySelector('[data-tm-cart-variant]');
  const price = card.querySelector('[data-tm-cart-price]');
  const why = card.querySelector('[data-tm-cart-why]');
  const impact = card.querySelector('[data-tm-cart-impact]');
  const remaining = section.querySelector('[data-tm-cart-remaining]');
  const input = card.querySelector('input[name="id"]');

  if (imageLink instanceof HTMLAnchorElement) imageLink.href = url;
  if (titleLink instanceof HTMLAnchorElement) { titleLink.href = url; titleLink.textContent = product.title; }
  if (image instanceof HTMLImageElement && product.image?.src) {
    image.src = product.image.src;
    image.alt = product.image.alt || product.title;
    image.hidden = false;
  }
  if (variantTitle) variantTitle.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : 'Standard';
  if (price) price.textContent = variant.priceFormatted || formatMoney(variant.price, 'CHF');
  if (why) why.textContent = (reasons || []).filter((reason) => reason !== GENERIC_REASON).slice(0, 2).join(' · ');
  setCommercialReason(card.querySelector('.tm-cart-recommendation__content'), commercialReason);
  const total = Number(cart?.total_price || 0);
  if (impact) impact.textContent = tmText('cart_estimate_reached', {amount: formatMoney(total + Number(variant.price), 'CHF')});
  if (remaining) remaining.textContent = tmText('remaining_threshold', {amount: formatMoney(Number(threshold) - total, 'CHF')});
  if (input instanceof HTMLInputElement) input.value = String(variant.id);
  card.hidden = false;
  section.hidden = false;
}

async function refreshCartSection(section, engine) {
  const previous = cartState.get(section);
  previous?.abortController.abort();
  const abortController = new AbortController();
  const generation = (previous?.generation || 0) + 1;
  cartState.set(section, {abortController, generation});

  const country = section.dataset.country;
  const currency = section.dataset.currency;
  const threshold = Number(section.dataset.thresholdCents || 0);
  if (country !== 'CH' || currency !== 'CHF' || threshold <= 0) {
    section.hidden = true;
    return;
  }

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: {Accept: 'application/json'}, credentials: 'same-origin', signal: abortController.signal,
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    const cart = await response.json();
    if (cartState.get(section)?.generation !== generation) return;
    if (!cart.item_count || cart.requires_shipping === false || cartHasGiftCard(cart) || Number(cart.total_price) >= threshold) {
      section.hidden = true;
      return;
    }

    if (!engine.hasPreferences()) {
      showFallback(section, cart, threshold);
      return;
    }

    const ranked = engine.rank(engine.catalogue, engine.catalogue.length);
    const match = chooseCartUpsell(ranked, cart, threshold, engine.getProfile());
    if (!match) {
      showNoCartMatch(section);
      return;
    }
    showCartMatch(section, match, cart, threshold);
  } catch (error) {
    if (error?.name !== 'AbortError') showNoCartMatch(section);
  }
}

function updateCartSections(engine) {
  document.querySelectorAll('[data-tm-cart-recommendation]').forEach((section) => refreshCartSection(section, engine));
}

function bindControls(engine) {
  document.querySelectorAll('[data-tm-profile-reset]').forEach((button) => {
    if (button.dataset.tmBound === 'true') return;
    button.dataset.tmBound = 'true';
    button.addEventListener('click', () => engine.reset());
  });
}

function refreshAll(engine) {
  bindControls(engine);
  updateHomeSections(engine);
  updatePersonalPicks(engine);
  updateCartSections(engine);
}

async function boot(engine) {
  await engine.ready;
  refreshAll(engine);
  engine.subscribe(() => refreshAll(engine));
  document.addEventListener('shopify:section:load', () => refreshAll(engine));
  document.addEventListener(CART_LINES_UPDATE_EVENT, (event) => {
    Promise.resolve(event.promise).catch(() => {}).finally(() => updateCartSections(engine));
  });
}

if (typeof window !== 'undefined') {
  if (window.TMFragrance?.ready) {
    boot(window.TMFragrance);
  } else {
    window.addEventListener('tm:fragrance-profile', () => {
      if (window.TMFragrance?.ready) boot(window.TMFragrance);
    }, {once: true});
  }
}
